import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Send, Zap, Target, TrendingUp, Users, Lightbulb } from 'lucide-react';
import { toast } from 'sonner';
import AppShell from '../components/AppShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUGGESTED_PROMPTS = [
  { text: "What should my 90-day action plan look like based on my archetype?", icon: Target },
  { text: "How can I leverage AI tools to accelerate my career pivot?", icon: Zap },
  { text: "What are the highest-impact moves I can make this week?", icon: TrendingUp },
  { text: "Help me prepare for a networking conversation about my pivot", icon: Users },
  { text: "What skills should I prioritize learning in the next 30 days?", icon: Lightbulb },
];

export default function AIGuide() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const user = JSON.parse(localStorage.getItem('pivot_user') || '{}');

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSend = async (messageText) => {
    const text = messageText || input.trim();
    if (!text) return;
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInput('');
    setLoading(true);
    try {
      const response = await axios.post(`${API}/ai/chat`, { user_id: user.user_id, message: text, session_id: sessionId });
      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) { toast.error('Failed to get response from Power Guide'); }
    finally { setLoading(false); }
  };

  const handleSubmit = (e) => { e.preventDefault(); handleSend(); };

  return (
    <AppShell>
      <div className="flex flex-col" style={{ height: 'calc(100vh - 52px)' }}>
        {/* Sub Header */}
        <div className="flex-shrink-0 px-4 md:px-8 py-3 border-b border-white/[0.06] flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
              <img src="https://customer-assets.emergentagent.com/job_clarity-network-1/artifacts/b1jdxi6j_image.png" alt="" className="w-6 h-6 rounded-lg" />
            </div>
            <div>
              <h1 className="text-sm font-inter font-semibold text-white" data-testid="ai-guide-title">Power Guide</h1>
              <p className="text-[#A39BB0] text-[10px] font-inter">Your strategic AI advisor</p>
            </div>
          </div>
          {sessionId && (
            <button onClick={() => { setMessages([]); setSessionId(null); }}
              className="text-[#A39BB0] hover:text-white text-xs font-inter transition-colors bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-full"
              data-testid="new-session-button">New Session</button>
          )}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 md:px-8 py-6">
          <div className="max-w-3xl mx-auto space-y-4">
            {messages.length === 0 ? (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="pt-8">
                <div className="text-center mb-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-[0_0_30px_rgba(168,85,247,0.3)]">
                    <img src="https://customer-assets.emergentagent.com/job_clarity-network-1/artifacts/b1jdxi6j_image.png" alt="" className="w-12 h-12 rounded-xl" />
                  </div>
                  <h2 className="text-2xl font-cormorant font-bold text-white mb-2">Welcome to Power Guide</h2>
                  <p className="text-[#A39BB0] font-inter text-sm max-w-md mx-auto">Your personal strategic advisor. Ask about your career path, AI strategy, or next moves.</p>
                </div>
                <div className="space-y-2">
                  <p className="text-[#A39BB0]/60 text-xs font-inter uppercase tracking-wider mb-3">Suggested Questions</p>
                  {SUGGESTED_PROMPTS.map((prompt, i) => {
                    const Icon = prompt.icon;
                    return (
                      <motion.button key={i} onClick={() => handleSend(prompt.text)}
                        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 * i }}
                        whileHover={{ x: 4 }}
                        className="w-full bg-white/[0.05] hover:bg-white/[0.08] border border-white/[0.08] hover:border-white/15 rounded-xl p-4 text-left transition-all group flex items-center space-x-3"
                        data-testid={`suggested-prompt-${i}`}>
                        <div className="w-8 h-8 bg-white/5 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-purple-500/20 transition-all">
                          <Icon className="w-4 h-4 text-[#A39BB0] group-hover:text-purple-300 transition-colors" />
                        </div>
                        <span className="text-[#A39BB0] font-inter text-sm group-hover:text-white transition-colors">{prompt.text}</span>
                      </motion.button>
                    );
                  })}
                </div>
              </motion.div>
            ) : (
              messages.map((msg, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] ${
                    msg.role === 'user'
                      ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-2xl rounded-br-md px-5 py-3 shadow-[0_0_15px_rgba(168,85,247,0.2)]'
                      : 'bg-white/[0.07] border border-white/[0.08] text-white/80 rounded-2xl rounded-bl-md px-5 py-4'
                  }`} data-testid={`message-${msg.role}`}>
                    <p className="whitespace-pre-wrap font-inter text-sm leading-relaxed">{msg.content}</p>
                  </div>
                </motion.div>
              ))
            )}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white/[0.07] border border-white/[0.08] rounded-2xl rounded-bl-md px-5 py-4">
                  <div className="flex space-x-1.5">
                    <div className="w-2 h-2 rounded-full bg-purple-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 rounded-full bg-purple-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 rounded-full bg-purple-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input */}
        <div className="flex-shrink-0 border-t border-white/[0.06] bg-[#1A0533]/80 backdrop-blur-xl px-4 md:px-8 py-4">
          <form onSubmit={handleSubmit} className="max-w-3xl mx-auto flex space-x-3">
            <input type="text" value={input} onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Power Guide anything..."
              className="flex-1 bg-white/[0.05] border border-white/[0.08] rounded-xl px-4 py-3 text-white focus:border-purple-500/50 outline-none transition-all placeholder:text-[#A39BB0]/40 font-inter text-sm"
              data-testid="ai-chat-input" />
            <motion.button type="submit" disabled={loading || !input.trim()}
              whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-purple-600 to-pink-500 text-white font-inter font-bold px-5 rounded-xl flex items-center space-x-2 transition-all disabled:opacity-40 shadow-[0_0_15px_rgba(168,85,247,0.3)]"
              data-testid="ai-chat-send-button">
              <Send className="w-4 h-4" />
            </motion.button>
          </form>
        </div>
      </div>
    </AppShell>
  );
}
